import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { autofillValueMatches, type AutofillSuggestion } from "@aam/shared";
import { useFormStore } from "../store/formStore";
import { revealAutofillField, type PortalAutofillPlan } from "../lib/contractAutofill";

export type { PortalAutofillPlan };

interface AutofillContextValue {
  plan: PortalAutofillPlan | null;
  /** Suggestion for a field, or null when nothing was carried forward. */
  suggestionFor: (fieldId: string) => AutofillSuggestion | null;
  /** True while the field still holds the value autofill supplied. */
  isAutofilled: (fieldId: string) => boolean;
  /** Fields carried forward that the agent should confirm before submitting. */
  reviewFieldIds: string[];
  /** Pulls one stored PII value forward, after an explicit request. */
  reveal: (fieldId: string) => Promise<void>;
  revealing: string | null;
  revealError: { fieldId: string; message: string } | null;
  /** Clears an autofilled value so the agent can start that answer over. */
  clearField: (fieldId: string) => void;
  dismissed: boolean;
  dismiss: () => void;
}

const AutofillContext = createContext<AutofillContextValue | null>(null);

export function AutofillProvider({
  plan,
  children,
}: {
  plan: PortalAutofillPlan | null;
  children: ReactNode;
}) {
  const formData = useFormStore((state) => state.formData);
  const updateFormData = useFormStore((state) => state.updateFormData);
  const markFieldEncrypted = useFormStore((state) => state.markFieldEncrypted);
  const [revealing, setRevealing] = useState<string | null>(null);
  const [revealError, setRevealError] = useState<{ fieldId: string; message: string } | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const byField = useMemo(() => {
    const map = new Map<string, AutofillSuggestion>();
    for (const suggestion of plan?.suggestions ?? []) {
      map.set(suggestion.fieldId, suggestion);
    }
    return map;
  }, [plan]);

  const suggestionFor = useCallback(
    (fieldId: string) => byField.get(fieldId) ?? null,
    [byField]
  );

  const isAutofilled = useCallback(
    (fieldId: string) => {
      const suggestion = byField.get(fieldId);
      return Boolean(suggestion && autofillValueMatches(suggestion, formData[fieldId]));
    },
    [byField, formData]
  );

  const reveal = useCallback(
    async (fieldId: string) => {
      setRevealing(fieldId);
      setRevealError(null);
      try {
        const revealed = await revealAutofillField(fieldId);
        // The SSN input holds digits only; an older submission may have stored
        // the dashed form the agent typed.
        const value =
          fieldId === "ssn" ? revealed.value.replace(/\D/g, "") : revealed.value;
        updateFormData(fieldId, value);
        markFieldEncrypted(fieldId);
      } catch (error) {
        setRevealError({
          fieldId,
          message: error instanceof Error ? error.message : "Could not load that value",
        });
      } finally {
        setRevealing(null);
      }
    },
    [markFieldEncrypted, updateFormData]
  );

  const clearField = useCallback(
    (fieldId: string) => {
      updateFormData(fieldId, Array.isArray(formData[fieldId]) ? [] : "");
    },
    [formData, updateFormData]
  );

  const reviewFieldIds = useMemo(
    () => (plan?.reviewFieldIds ?? []).filter((fieldId) => isAutofilled(fieldId)),
    [plan, isAutofilled]
  );

  const value = useMemo(
    () => ({
      plan,
      suggestionFor,
      isAutofilled,
      reviewFieldIds,
      reveal,
      revealing,
      revealError,
      clearField,
      dismissed,
      dismiss: () => setDismissed(true),
    }),
    [
      plan,
      suggestionFor,
      isAutofilled,
      reviewFieldIds,
      reveal,
      revealing,
      revealError,
      clearField,
      dismissed,
    ]
  );

  return <AutofillContext.Provider value={value}>{children}</AutofillContext.Provider>;
}

/**
 * Autofill is portal-only, so every consumer has to cope with its absence —
 * the public form renders the same components with no provider above them.
 */
export function useAutofill(): AutofillContextValue | null {
  return useContext(AutofillContext);
}
